"use client";

import Image from "next/image";
import { useEffect, useState, memo, useMemo } from "react";
import { useRouter } from "next/router";


const ctcData = {
  "/performance-marketing-mentorship-program": "₹ 8,00,000+",
  "/digital-marketing-mentorship-program": "₹ 15,00,000+",
  "/ui-ux-design-mentorship-program": "₹ 8,00,000+",
  "/product-management-mentorship-program": "₹ 15,00,000+",
  "/management-consulting-mentorship-program": "₹ 15,00,000+",
  "/default": "₹ 10,00,000+"
};

const StatsCards = () => {
  const [isMobile, setIsMobile] = useState(false);
    const [hoveredCard, setHoveredCard] = useState(null);
   const [currentPath, setCurrentPath] = useState("");

  const currentCTC = useMemo(() => {
    return ctcData[currentPath] || ctcData["/default"];
  }, [currentPath]);

  const companyLogos = useMemo(() => [
    { src: "https://d3kl8zsmmx4oop.cloudfront.net/Google_c2d8e527c7.svg", alt: "Google" },
    { src: "https://d3kl8zsmmx4oop.cloudfront.net/layer1_001742dcec.svg", alt: "Swiggy" },
    { src: "https://d3kl8zsmmx4oop.cloudfront.net/Vector_8abe02abf9.svg", alt: "Company 1" },
    { src: "https://d3kl8zsmmx4oop.cloudfront.net/Group_77e1b17517.svg", alt: "Company 2" },
    { src: "https://d3kl8zsmmx4oop.cloudfront.net/Vector_1_3f59974b49.svg", alt: "Company 3" },
    { src: "https://d3kl8zsmmx4oop.cloudfront.net/Philips_1_c50dd5b8cb.svg", alt: "Philips" },
    { src: "https://d3kl8zsmmx4oop.cloudfront.net/LOGO_07fbe3c627.svg", alt: "Company 4" }
  ], []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    const updatePath = () => {
      if (typeof window !== 'undefined') {
        setCurrentPath(window.location.pathname);
      }
    };

    handleResize();
    updatePath();
    
    window.addEventListener("resize", handleResize);
    window.addEventListener("popstate", updatePath);
    
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("popstate", updatePath);
    };
  }, []);

  // Card images for normal state
  const cardImages = {
    card1: "https://d31bwppm8yl9g2.cloudfront.net/learner/website/rs.svg",
    card2: "https://d31bwppm8yl9g2.cloudfront.net/learner/website/pap.svg",
    card4: "https://d31bwppm8yl9g2.cloudfront.net/learner/website/fold.svg",
    card5: "https://d31bwppm8yl9g2.cloudfront.net/learner/website/peop.svg",
  };

  // Card images for hover state
  const cardHoverImages = {
    card1: "https://d31bwppm8yl9g2.cloudfront.net/learner/website/rsg.svg", // Assuming alternate image exists
    card2: "https://d31bwppm8yl9g2.cloudfront.net/learner/website/papg.svg", // Assuming alternate image exists
    card4: "https://d31bwppm8yl9g2.cloudfront.net/learner/website/foldg.svg", // Assuming alternate image exists
    card5: "https://d31bwppm8yl9g2.cloudfront.net/learner/website/peopg.svg", // Assuming alternate image exists
  };

  // Fallback to original images if hover images don't exist
  const getCardImage = (cardKey) => {
    if (hoveredCard === cardKey && cardHoverImages[cardKey]) {
      return cardHoverImages[cardKey];
    }
    return cardImages[cardKey];
  };

  return (
    <div className="flex justify-center items-center w-full">
      <div
        className={`${
          isMobile ? "w-[90%]" : "w-[80%]"
        } flex flex-col md:flex-row flex-wrap gap-6`}
      >
        {/* Left Column - 2 Cards */}
        <div className="w-full md:w-[30%] flex flex-col gap-6">
          {/* Card 1 - Annual CTC */}
          <div
            className="bg-[#fffaf1] rounded-2xl p-6 flex flex-col justify-between h-28 md:h-40 relative transition-all duration-300  cursor-pointer border border-[hsla(176,8%,62%,0.4)]"
            onMouseEnter={() => setHoveredCard("card1")}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div className="z-10">
              <h2 className="text-2xl font-medium text-gray-800">{currentCTC}</h2>
              <p className="text-sm text-gray-600 font-normal uppercase">
                Average annual CTC
              </p>
            </div>
            <div className="absolute right-6 top-1/2 transform -translate-y-1/2">
              <img
                src={getCardImage("card1")}
                alt="Rupee symbol"
                className="h-24 w-24 transition-all duration-300"
              />
            </div>
          </div>

          {/* Card 2 - Secret Insights */}
          <div
            className="bg-[#fffaf1] rounded-2xl p-6 flex flex-col justify-between h-28 md:h-40 relative transition-all duration-300  cursor-pointer border border-[hsla(176,8%,62%,0.4)]"
            onMouseEnter={() => setHoveredCard("card2")}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div className="z-10">
              <h2 className="text-2xl font-medium text-gray-800">
                Secret insights
              </h2>
              <p className="text-sm text-gray-600 font-normal uppercase">
                From real case-studies
              </p>
            </div>
            <div className="absolute bottom-0 right-0">
              <img
                src={getCardImage("card2")}
                alt="Documents"
                className="h-18 w-auto transition-all duration-300"
              />
            </div>
          </div>
        </div>

        {/* Center Column - Company Logos Marquee - Full Height */}
        <div className="w-full md:w-[35%] bg-[#fffaf1] rounded-2xl p-6 flex flex-col justify-between  relative border border-[hsla(176,8%,62%,0.4)]">
          <div className="text-center mb-4">
            <h2 className="text-2xl font-medium text-gray-800">
              Top companies hiring in this industry
            </h2>
            <p className="text-sm text-gray-600 font-normal uppercase mt-2">
              Get hired to work for these global giants
            </p>
          </div>

          <div className="flex flex-col justify-center space-y-16 flex-grow ">
            {/* First row of logos - marquee */}
            <div className="overflow-hidden">
              <div className="marquee-wrapper">
                <div className="marquee-content">
                  <div className="flex items-center justify-around w-full gap-10">
                    <img
                      src="https://d3kl8zsmmx4oop.cloudfront.net/Google_c2d8e527c7.svg"
                      alt="Google"
                      className="h-8"
                    />
                    <img
                      src="https://d3kl8zsmmx4oop.cloudfront.net/layer1_001742dcec.svg"
                      alt="Swiggy"
                      className="h-6 md:h-8"
                    />
                    <img
                      src="https://d3kl8zsmmx4oop.cloudfront.net/Vector_8abe02abf9.svg"
                      alt="Zoho"
                        className="h-6 md:h-8"
                    />
                    <img
                      src="https://d3kl8zsmmx4oop.cloudfront.net/Group_77e1b17517.svg"
                      alt="Zoho"
                       className="h-6 md:h-8"
                    />
                    <img
                      src="https://d3kl8zsmmx4oop.cloudfront.net/Vector_1_3f59974b49.svg"
                      alt="Zoho"
                       className="h-6 md:h-8"
                    />
                    <img
                      src="https://d3kl8zsmmx4oop.cloudfront.net/Philips_1_c50dd5b8cb.svg"
                      alt="Zoho"
                    className="h-6 md:h-8"
                    />
                    <img
                      src="https://d3kl8zsmmx4oop.cloudfront.net/LOGO_07fbe3c627.svg"
                      alt="Zoho"
                        className="h-6 md:h-8"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Second row of logos - marquee in opposite direction */}
            <div className="overflow-hidden">
              <div className="marquee-wrapper">
                <div className="marquee-content-reverse">
                  <div className="flex items-center justify-around w-full gap-10">
                  <img
                      src="https://d3kl8zsmmx4oop.cloudfront.net/Google_c2d8e527c7.svg"
                      alt="Google"
                         className="h-6 md:h-8"
                    />
                    <img
                      src="https://d3kl8zsmmx4oop.cloudfront.net/layer1_001742dcec.svg"
                      alt="Swiggy"
                        className="h-6 md:h-8"
                    />
                    <img
                      src="https://d3kl8zsmmx4oop.cloudfront.net/Vector_8abe02abf9.svg"
                      alt="Zoho"
                        className="h-6 md:h-8"
                    />
                    <img
                      src="https://d3kl8zsmmx4oop.cloudfront.net/Group_77e1b17517.svg"
                      alt="Zoho"
                        className="h-6 md:h-8"
                    />
                    <img
                      src="https://d3kl8zsmmx4oop.cloudfront.net/Vector_1_3f59974b49.svg"
                      alt="Zoho"
                         className="h-6 md:h-8"
                    />
                    <img
                      src="https://d3kl8zsmmx4oop.cloudfront.net/Philips_1_c50dd5b8cb.svg"
                      alt="Zoho"
                        className="h-6 md:h-8"
                    />
                    <img
                      src="https://d3kl8zsmmx4oop.cloudfront.net/LOGO_07fbe3c627.svg"
                      alt="Zoho"
                         className="h-6 md:h-8"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - 2 Cards */}
        <div className="w-full md:w-[30%] flex flex-col gap-6">
          {/* Card 4 - Multiple Projects */}
          <div
            className="bg-[#fffaf1] rounded-2xl p-6 flex flex-col justify-between h-28 md:h-40 relative transition-all duration-300 cursor-pointer border border-[hsla(176,8%,62%,0.4)]"
            onMouseEnter={() => setHoveredCard("card4")}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div className="z-10">
              <h2 className="text-2xl font-medium text-gray-800">
                Multiple projects
              </h2>
              <p className="text-sm font-normal text-gray-600 uppercase">Learn by doing</p>
            </div>
            <div className="absolute bottom-0 right-0">
              <img
                src={getCardImage("card4")}
                alt="Projects"
                className="h-18 w-auto transition-all duration-300"
              />
            </div>
          </div>

          {/* Card 5 - Live QnA */}
          <div
            className="bg-[#fffaf1] rounded-2xl p-6 flex flex-col justify-between h-28 md:h-40 relative transition-all duration-300 cursor-pointer border border-[hsla(176,8%,62%,0.4)]"
            onMouseEnter={() => setHoveredCard("card5")}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div className="z-10">
              <h2 className="text-2xl font-medium text-gray-800">
                Live QnA sessions
              </h2>
              <p className="text-sm font-normal text-gray-600 uppercase">
                Talk with industry mentors
              </p>
            </div>
            <div className="absolute bottom-0 right-0">
              <img
                src={getCardImage("card5")}
                alt="Mentors"
                className="h-18 w-auto transition-all duration-300"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function StatsCardsWrapper() {
  return (
    <>
      <style jsx global>{`
        .marquee-wrapper {
          width: 100%;
          overflow: hidden;
        }

        .marquee-content {
          display: flex;
          animation: marquee 20s linear infinite;
          width: 200%;
        }

        .marquee-content-reverse {
          display: flex;
          animation: marquee-reverse 20s linear infinite;
          width: 200%;
        }

        .marquee-content > div,
        .marquee-content-reverse > div {
          width: 100%;
          flex-shrink: 0;
        }

        @keyframes marquee {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-100%);
          }
        }

        @keyframes marquee-reverse {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(0);
          }
        }
      `}</style>
      <StatsCards />
    </>
  );
}
